import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, ClockCounterClockwise, GearSix, SignOut, Buildings, ChartBar, Megaphone, CreditCard, Clock, LockKey, WhatsappLogo, Pulse, Lifebuoy, CaretDoubleLeft, CaretDoubleRight, House, Sparkle, ShareNetwork, FunnelSimple, Broadcast } from "@phosphor-icons/react";
import FloatingDialer from "@/components/FloatingDialer";
import PresenceControl from "@/components/PresenceControl";

const NAV_GROUPS = ["TOP", "VOICE", "MESSAGING", "SOCIAL", "ANALYTICS", "WORKSPACE"];

export default function AppShell({ children, onCallCompleted }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const feat = user?.features || {};
    const callCenter = feat.call_center !== false;  // WhatsApp Standalone hides the call center
    const access = user?.product_access || ["calling", "whatsapp", "social"];
    const canCall = access.includes("calling");
    const canWa = access.includes("whatsapp");
    const canSocial = access.includes("social");
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem("nav_collapsed") === "1");
    const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("nav_collapsed", c ? "0" : "1"); return !c; });
    const [ccLapsedHidden, setCcLapsedHidden] = useState(() => localStorage.getItem("cc_lapsed_dismissed") === "1");
    const dismissCcLapsed = () => { localStorage.setItem("cc_lapsed_dismissed", "1"); setCcLapsedHidden(true); };

    const navItems = [];
    navItems.push({ to: "/", label: "Overview", icon: House, testid: "nav-overview", group: "TOP" });
    if (callCenter && canCall) {
        navItems.push({ to: "/calls", label: "Call History", icon: ClockCounterClockwise, testid: "nav-calls", group: "VOICE" });
    }
    if (callCenter && canCall && (user?.role === "admin" || user?.role === "supervisor")) {
        navItems.push({ to: "/campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-campaigns", locked: !feat.campaigns, group: "VOICE" });
    }
    if (callCenter && canCall && user?.role === "agent") {
        navItems.push({ to: "/my-campaigns", label: "Campaigns", icon: Megaphone, testid: "nav-my-campaigns", locked: !feat.campaigns, group: "VOICE" });
    }
    // WhatsApp-Standalone: no calling access — show an enticing "Add Call Center" teaser instead.
    if (!callCenter && user?.role === "admin") {
        navItems.push({ to: "/billing#calling-plans", label: "Call Center", icon: PhoneCall, testid: "nav-add-calling", upsell: true, group: "VOICE" });
    }
    if (canWa) {
        navItems.push({ to: "/whatsapp", label: "WhatsApp", icon: WhatsappLogo, testid: "nav-whatsapp", locked: !feat.whatsapp, group: "MESSAGING" });
    }
    if (canSocial) {
        navItems.push({ to: "/social", label: "Social Hub", icon: ShareNetwork, testid: "nav-social", locked: !feat.social, group: "SOCIAL" });
    }
    navItems.push({ to: "/activity", label: "Activity", icon: Pulse, testid: "nav-activity", group: "ANALYTICS" });
    if (callCenter && canCall && (user?.role === "admin" || user?.role === "supervisor")) {
        navItems.push({ to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports", locked: !feat.reports, group: "ANALYTICS" });
    }
    if (callCenter && canCall) {
        navItems.push({ to: "/support", label: "Support", icon: Lifebuoy, testid: "nav-support", locked: !feat.support_tickets, group: "WORKSPACE" });
    }
    if (user?.role === "admin") {
        navItems.push({ to: "/settings", label: "Settings", icon: GearSix, testid: "nav-settings", group: "WORKSPACE" });
        navItems.push({ to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing", group: "WORKSPACE" });
    }

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const imp = user?.impersonation;
    const exitImpersonation = async () => {
        try {
            await api.post("/auth/stop-impersonation");
            window.location.assign("/platform");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const renderNavRow = ({ to, label, icon: Icon, testid, locked, upsell }) => {
        const base = `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${collapsed ? "justify-center" : ""}`;
        if (upsell) {
            return (
                <button
                    key={to}
                    data-testid={testid}
                    onClick={() => navigate("/billing#calling-plans")}
                    title={collapsed ? `${label} — add voice calling` : "Add voice calling to your workspace"}
                    className={`${base} text-brand hover:bg-brand/10`}
                >
                    <Icon size={18} weight="fill" className="shrink-0" />
                    {!collapsed && <>
                        <span className="font-medium">{label}</span>
                        <span data-testid={`${testid}-badge`} className="ml-auto inline-flex items-center gap-1 rounded-full bg-brand/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand">
                            <Sparkle size={10} weight="fill" /> Add
                        </span>
                    </>}
                </button>
            );
        }
        if (locked) {
            return (
                <button
                    key={to}
                    data-testid={testid}
                    onClick={() => navigate(user?.role === "admin" ? "/billing" : "/")}
                    title={collapsed ? `${label} — upgrade to unlock` : "Upgrade to Professional to unlock"}
                    className={`${base} text-stone-400 hover:bg-stone-50`}
                >
                    <Icon size={18} weight="regular" className="shrink-0" />
                    {!collapsed && <>
                        <span className="font-medium">{label}</span>
                        <span data-testid={`${testid}-lock`} className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">
                            <LockKey size={10} weight="fill" /> Pro
                        </span>
                    </>}
                </button>
            );
        }
        return (
            <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={testid}
                title={collapsed ? label : undefined}
                className={({ isActive }) => `${base} ${isActive ? "bg-ink text-white shadow-sm" : "text-stone2 hover:bg-stone-100 hover:text-ink"}`}
            >
                <Icon size={18} weight="regular" className="shrink-0" />
                {!collapsed && <span className="font-medium">{label}</span>}
            </NavLink>
        );
    };

    return (
        <div className="flex h-screen w-full overflow-hidden bg-paper text-ink">
            {/* Sidebar */}
            <aside className={`hidden shrink-0 border-r border-stone-200 bg-white transition-[width] duration-300 ease-out md:flex md:flex-col ${collapsed ? "w-20" : "w-64"}`}>
                <div className={`flex items-center gap-2.5 px-5 py-4 ${collapsed ? "justify-center px-0" : ""}`}>
                    <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl border border-stone-200 bg-white">
                        <img src="/inspiro-connect-mark.png" alt="Inspiro Connect" className="h-full w-full object-contain" />
                    </div>
                    {!collapsed && (
                        <div className="leading-tight">
                            <div className="font-heading text-lg font-bold">Inspiro Connect</div>
                            <div className="whitespace-nowrap text-[8px] font-semibold uppercase tracking-normal text-stone2">Omnichannel · Unified · Intelligent</div>
                        </div>
                    )}
                </div>

                <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-1">
                    {NAV_GROUPS.map((g) => {
                        const items = navItems.filter((n) => n.group === g);
                        if (!items.length) return null;
                        return (
                            <div key={g} className="flex flex-col gap-1">
                                {g === "TOP" ? null : (collapsed
                                    ? <div className="mx-3 mb-1 border-t border-stone-100" />
                                    : <div className="mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-stone2/70">{g}</div>)}
                                {items.map(renderNavRow)}
                            </div>
                        );
                    })}
                </nav>

                <div className={`border-t border-stone-200 ${collapsed ? "flex flex-col items-center gap-2 p-2" : "p-3"}`}>
                    {!collapsed && <PresenceControl />}
                    {collapsed ? (
                        <>
                            <button data-testid="nav-collapse-toggle" onClick={toggleCollapsed} aria-label="Expand sidebar" title="Expand" className="rounded-full p-2 text-stone2 transition-colors hover:bg-stone-100 hover:text-ink">
                                <CaretDoubleRight size={18} />
                            </button>
                            <button data-testid="logout-button" onClick={handleLogout} title="Sign out" className="rounded-full p-2 text-stone2 transition-colors hover:bg-stone-100 hover:text-ink">
                                <SignOut size={18} />
                            </button>
                        </>
                    ) : (
                        <div className="mt-2 flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <div className="truncate text-sm font-semibold" data-testid="user-name">{user?.name}</div>
                                <div className="flex items-center gap-1 truncate text-[11px] text-stone2">
                                    <Buildings size={11} className="shrink-0" />
                                    <span className="truncate">{user?.org_name || "Workspace"}</span>
                                    <span className="shrink-0 uppercase tracking-wider">· {user?.role}</span>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-0.5">
                                <button data-testid="nav-collapse-toggle" onClick={toggleCollapsed} aria-label="Collapse sidebar" title="Collapse" className="rounded-full p-2 text-stone2 transition-colors hover:bg-stone-100 hover:text-ink">
                                    <CaretDoubleLeft size={17} />
                                </button>
                                <button data-testid="logout-button" onClick={handleLogout} className="rounded-full p-2 text-stone2 transition-colors hover:bg-stone-100 hover:text-ink" title="Sign out">
                                    <SignOut size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto">
                {imp && (
                    <div
                        data-testid="impersonation-banner"
                        className={`sticky top-0 z-30 flex flex-wrap items-center justify-between gap-3 border-b px-6 py-2.5 text-sm text-white ${imp.readonly ? "border-amber-400 bg-amber-600" : "border-indigo-300 bg-indigo-600"}`}
                    >
                        <span className="font-medium">
                            {imp.readonly ? "Read-only support session · " : ""}
                            You are viewing <b>{imp.target_org_name || "this workspace"}</b>
                            {imp.readonly ? " (view-only — changes are disabled)" : " as a support session"}
                            {imp.impersonator_email ? ` · ${imp.impersonator_email}` : ""}.
                        </span>
                        <button
                            data-testid="exit-impersonation-btn"
                            onClick={exitImpersonation}
                            className="rounded-full bg-white/15 px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-all hover:-translate-y-0.5 hover:bg-white/25"
                        >
                            Exit to platform
                        </button>
                    </div>
                )}
                {user?.org_status === "suspended" && (
                    <div
                        data-testid="suspended-banner"
                        className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-6 py-2.5 text-sm font-medium text-red-700"
                    >
                        <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                        Your workspace is suspended (read-only). Outbound calling is disabled — please contact your provider.
                    </div>
                )}
                {user?.role !== "super_admin" && user?.subscription_status === "trialing" && (
                    <div className="flex justify-end px-6 pt-4">
                        {user?.role === "admin" ? (
                            <NavLink
                                to="/billing"
                                data-testid="trial-countdown-chip"
                                className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3.5 py-1.5 text-xs font-semibold text-amber-800 transition-all hover:-translate-y-0.5 hover:bg-amber-100"
                            >
                                <Clock size={14} weight="fill" />
                                {user?.trial_days_left > 0
                                    ? `${user.trial_days_left} day${user.trial_days_left === 1 ? "" : "s"} left in trial`
                                    : "Trial ends today"}
                                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] uppercase tracking-wider text-white">Upgrade</span>
                            </NavLink>
                        ) : (
                            <span
                                data-testid="trial-countdown-chip"
                                className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3.5 py-1.5 text-xs font-semibold text-amber-800"
                            >
                                <Clock size={14} weight="fill" />
                                {user?.trial_days_left > 0 ? `${user.trial_days_left} days left in trial` : "Trial ends today"}
                            </span>
                        )}
                    </div>
                )}
                {user?.role !== "super_admin" && user?.entitled === false && (
                    <div
                        data-testid="trial-expired-banner"
                        className="flex flex-wrap items-center justify-between gap-3 border-b border-red-200 bg-red-50 px-6 py-2.5 text-sm"
                    >
                        <span className="font-medium text-red-700">
                            Your free trial has ended. {user?.role === "admin" ? "Upgrade your plan to restore access." : "Contact your workspace admin to restore access."}
                        </span>
                        {user?.role === "admin" && (
                            <NavLink
                                to="/billing"
                                data-testid="expired-upgrade-btn"
                                className="rounded-full bg-brand px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover"
                            >
                                Upgrade now
                            </NavLink>
                        )}
                    </div>
                )}
                {user?.role === "admin" && user?.calling_lapsed && !ccLapsedHidden && (
                    <div
                        data-testid="calling-lapsed-banner"
                        className="flex flex-wrap items-center justify-between gap-3 border-b border-brand/25 bg-brand/5 px-6 py-2.5 text-sm"
                    >
                        <span className="flex items-center gap-2 font-medium text-ink">
                            <PhoneCall size={16} weight="fill" className="text-brand" />
                            Your calling trial has ended — your WhatsApp is still active. Add a calling plan to bring the phone back.
                        </span>
                        <div className="flex items-center gap-2">
                            <NavLink
                                to="/billing#calling-plans"
                                data-testid="calling-lapsed-cta"
                                className="rounded-full bg-brand px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover"
                            >
                                Add calling
                            </NavLink>
                            <button
                                data-testid="calling-lapsed-dismiss"
                                onClick={dismissCcLapsed}
                                className="grid h-7 w-7 place-items-center rounded-full text-lg leading-none text-stone2 transition-colors hover:bg-stone-200/60 hover:text-ink"
                                title="Dismiss"
                            >
                                <span aria-hidden>×</span>
                                <span className="sr-only">Dismiss</span>
                            </button>
                        </div>
                    </div>
                )}
                {children}
            </main>
            {callCenter && canCall && <FloatingDialer onCallCompleted={onCallCompleted} />}
        </div>
    );
}
